import React, { useEffect, useMemo, useRef, useState } from 'react'
import { Icon } from '@pff-consumer/web-ui/components/icon/icon'
import { checkoutHooks } from '@pff-consumer/data-provider/lib/checkout/checkout.hooks'
import { SVGGroup } from '@pff-consumer/utils/lib/svg/svg-group'
import { useOutsideClick } from '@pff-consumer/tweed-ui/hooks/use-outside-click.web'
import { useEscapeKey } from '@pff-consumer/tweed-ui/hooks/use-escape-key.web'
import { useSubscribeAnalyticsEvent } from '@pff-consumer/feature-account/hooks/use-subscribe-analytics-event.web'
import { useStyles } from './recurly-modal.styles'
import { FeaturesCarousel } from './features-carousel/features-carousel'
import { useUpsellPricing } from '../../../hooks/useUpsellPricing'
import { useUpsellModalQueryState } from '../../../hooks/useUpsellModalQueryState'
import { getDiscountPercent, sendCloseEvent, sendLinkClickedEvent } from '../../../utils/util-functions'

const { useWindowDimensions, useBreakpoints } = checkoutHooks

const COPY_TEXT = 'COPY CODE'
const THREE_SECONDS = 3000
const DECORATED_DISCOUNT_PERCENTAGE = 30

export const RecurlyModal = () => {
  const { width } = useWindowDimensions()
  const { isDesktop } = useBreakpoints()
  const [copyText, setCopyText] = useState<string>(COPY_TEXT)
  const modalRef = useRef<HTMLDivElement>(null)
  const { promoCode, carouselCards, header, parent } = useUpsellModalQueryState()
  const { sendSubscribeAnalyticsEvent } = useSubscribeAnalyticsEvent()
  const styles = useStyles({ width, isDesktop })
  const url = new URL(parent)

  const onClose = () => sendCloseEvent(url.origin)

  useOutsideClick(onClose, modalRef)
  useEscapeKey(onClose)

  useEffect(() => {
    if (copyText === 'COPIED!') {
      setTimeout(() => setCopyText(COPY_TEXT), THREE_SECONDS)
    }
  }, [copyText])

  const { annualPlanPrice, annualPriceLoading, monthlyPlanPrice, monthlyPriceLoading, isError } =
    useUpsellPricing(promoCode)

  const percentageDiscount = useMemo(
    () => getDiscountPercent(Number(annualPlanPrice.now.total), Number(annualPlanPrice.next.total)),
    [annualPlanPrice]
  )

  const showToastMessage = () => {
    const toastMessage = document.getElementById('toastMessage') as HTMLElement
    toastMessage.className = styles.toastMessageShow
    setTimeout(() => {
      toastMessage.className = styles.toastMessage
    }, THREE_SECONDS)
  }

  const copyCode = () => {
    navigator.clipboard.writeText(promoCode)
    setCopyText('COPIED!')
    showToastMessage()
  }

  const subscribeButtonText =
    !annualPriceLoading && !monthlyPriceLoading && !isError && promoCode
      ? `Get PFF+ for ${percentageDiscount}% Off Now!`
      : 'SUBSCRIBE NOW'

  const onSubscribe = () => {
    sendSubscribeAnalyticsEvent('CTA Clicked', {
      ctaName: subscribeButtonText,
      ctaLocation: 'Upsell Modal - Variant 1',
      path: url.pathname,
    })
    sendLinkClickedEvent(url.origin)
    if (promoCode) {
      copyCode()
    }
  }

  const planPriceDescription = `${percentageDiscount}% OFF`

  return (
    <div
      data-testid='recurlyModal'
      className={styles.containerOverlay}
    >
      <div
        className={styles.drawer}
        ref={modalRef}
      >
        <button
          onClick={onClose}
          className={styles.closeButton}
          type='button'
        >
          <Icon
            name='close'
            testID='close-icon'
            group={SVGGroup.Icons}
            width={24}
            height={24}
            className={styles.closeIcon}
          />
        </button>

        <Icon
          name='pff-plus-white'
          testID='white-pff-logo'
          group={SVGGroup.Icons}
          width={48}
          height={28}
        />

        <header className={styles.header}>{header}</header>

        {!annualPriceLoading && !monthlyPriceLoading && !isError && promoCode ? (
          <div className={styles.planPriceSection}>
            <div className={styles.planPriceDescriptionContainer}>
              <div className={styles.planPriceDescriptionLineOne}>{planPriceDescription}</div>
              <div className={styles.planPrice}>
                <span className={styles.totalPrice}>
                  {annualPlanPrice.currency.symbol}
                  {annualPlanPrice.base.plan.unit}
                </span>{' '}
                now {annualPlanPrice.currency.symbol}
                {annualPlanPrice.next.total}/yr or {monthlyPlanPrice.currency.symbol}
                {monthlyPlanPrice.now.total}/mo
              </div>
            </div>
            <button
              onClick={onSubscribe}
              className={styles.mdsSubscribeButton}
              type='button'
              data-testid='upsellModal.subscribeButton'
            >
              <div className={styles.couponCode}>{promoCode.toUpperCase()}</div>
              <div className={styles.copyCode}>{copyText}</div>
            </button>
          </div>
        ) : (
          <button
            onClick={onSubscribe}
            className={styles.subscribeButton}
            type='button'
          >
            {subscribeButtonText}
          </button>
        )}

        {percentageDiscount === DECORATED_DISCOUNT_PERCENTAGE && (
          <picture className={styles.mdsStickerContainer}>
            <img
              src='/assets/images/mds-sticker.webp'
              alt='MDS sticker'
              className={styles.mdsSticker}
            />
          </picture>
        )}

        <FeaturesCarousel
          variant={isDesktop ? 'desktop' : 'mobile'}
          carouselCards={carouselCards}
        />

        <div
          id='toastMessage'
          className={styles.toastMessage}
        >
          <Icon
            name='checkmark-toast'
            group={SVGGroup.Icons}
            height={36}
          />
          <span className={styles.toastMessageText}>Code Copied Successfully</span>
        </div>
      </div>
    </div>
  )
}
